import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import { sample } from "lodash";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";

const VALUES = {
  sin: {
    0: "0",
    30: "\\dfrac{1}{2}",
    45: "\\dfrac{\\sqrt{2}}{2}",
    60: "\\dfrac{\\sqrt{3}}{2}",
    90: "1",
  },
  cos: {
    0: "1",
    30: "\\dfrac{\\sqrt{3}}{2}",
    45: "\\dfrac{\\sqrt{2}}{2}",
    60: "\\dfrac{1}{2}",
    90: "0",
  },
  tan: {
    0: "0",
    30: "\\dfrac{\\sqrt{3}}{3}",
    45: "1",
    60: "\\sqrt{3}",
    90: "nie istnieje",
  },
};

function simplifySqrtFractionLatex(numerator, denominator) {
  const cleaned = numerator
    .replace(/\\cdot\s*1$/, "")           // √3·1 → √3
    .replace(/^1\s*\\cdot\s*/, "")       // 1·√3 → √3
    .trim();

  // Jeżeli licznik zawiera mnożenie przez 0 → wynik to 0
  if (/0\s*\\cdot/.test(cleaned) || /\\cdot\s*0/.test(cleaned) || cleaned === "0") {
    return "0";
  }

  const sqrtMatch = cleaned.match(/^\\sqrt{(\d+)}$/);
  let num = cleaned;

  if (sqrtMatch) {
    const inside = parseInt(sqrtMatch[1]);
    const sqrtVal = Math.sqrt(inside);
    if (Number.isInteger(sqrtVal)) {
      num = `${sqrtVal}`;
    }
  }

  const numVal = parseInt(num);
  const denVal = parseInt(denominator);

  if (!isNaN(numVal) && !isNaN(denVal)) {
    const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
    const d = gcd(numVal, denVal);
    const n = numVal / d;
    const m = denVal / d;
    return m === 1 ? `${n}` : `\\dfrac{${n}}{${m}}`;
  }

  return `\\dfrac{${num}}{${denominator}}`;
}




export function generateTask() {
  let func1, angle1, func2, angle2, val1, val2;

  const NUMERIC_VALUES = {
    "0": 0,
    "1": 1,
    "\\dfrac{1}{2}": 0.5,
    "\\dfrac{\\sqrt{2}}{2}": Math.SQRT2 / 2,
    "\\dfrac{\\sqrt{3}}{2}": Math.sqrt(3) / 2,
    "\\dfrac{\\sqrt{3}}{3}": Math.sqrt(3) / 3,
    "\\sqrt{3}": Math.sqrt(3),
  };

  do {
    func1 = sample(["sin", "cos", "tan"]);
    angle1 = sample([0, 30, 45, 60, 90]);
    val1 = VALUES[func1][angle1];

    func2 = sample(["sin", "cos", "tan"]);
    angle2 = sample([0, 30, 45, 60, 90]);
    val2 = VALUES[func2][angle2];
  } while (
    val1 === "nie istnieje" ||
    val2 === "nie istnieje" ||
    NUMERIC_VALUES[val1] == null ||
    NUMERIC_VALUES[val2] == null
  );

  const numerical = NUMERIC_VALUES[val1] * NUMERIC_VALUES[val2];

  // Symboliczne przekształcenie wyrażenia typu \dfrac{a}{b}
function multiplyLatexFractionsSimplified(latex1, latex2) {
  const extract = (s) => {
    const fracMatch = s.match(/\\dfrac{(.+)}{(.+)}/);
    if (fracMatch) return { num: fracMatch[1], denom: fracMatch[2] };

    const rootMatch = s.match(/^\\sqrt{(.+)}$/);
    if (rootMatch) return { num: `\\sqrt{${rootMatch[1]}}`, denom: "1" };

    if (/^\d+$/.test(s)) return { num: s, denom: "1" };

    return { num: s, denom: "1" };
  };

  const f1 = extract(latex1);
  const f2 = extract(latex2);

  const sqrtRe = /^\\sqrt{(\d+)}$/;
  const num1 = f1.num.match(sqrtRe);
  const num2 = f2.num.match(sqrtRe);

  let simplifiedNumerator;
  if (num1 && num2) {
    const n1 = parseInt(num1[1]);
    const n2 = parseInt(num2[1]);
    simplifiedNumerator = `\\sqrt{${n1 * n2}}`;
  } else {
    simplifiedNumerator = `${f1.num} \\cdot ${f2.num}`;
  }

  const d1 = parseInt(f1.denom);
  const d2 = parseInt(f2.denom);
  const simplifiedDenominator = !isNaN(d1) && !isNaN(d2) ? `${d1 * d2}` : `${f1.denom} \\cdot ${f2.denom}`;

  return simplifySqrtFractionLatex(simplifiedNumerator, simplifiedDenominator);

}


const symbolicProduct = multiplyLatexFractionsSimplified(val1, val2);

const expectedLatex = `${val1} \\cdot ${val2} = ${symbolicProduct}`;

const task = {
  type: "MultiplyTrigFunctionsTask",
  func1,
  angle1,
  func2,
  angle2,
  value1: val1,
  value2: val2,
  expectedAnswer: numerical.toString(),
  expectedLatex,
};

return {
  ...task,
  data: task, // ⬅️ kluczowe, żeby renderExplanation działał w quizie
};

}


export function renderTask({ func1, angle1, func2, angle2 }) {
  return (
    <p>
      Oblicz wartość wyrażenia:{" "}
      <InlineMath math={`\\${func1}(${angle1}^\\circ) \\cdot \\${func2}(${angle2}^\\circ)`} />
    </p>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const label = `\\${task.func1}(${task.angle1}^\\circ) \\cdot \\${task.func2}(${task.angle2}^\\circ)`;
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={label}
    />
  );
}

export function renderUserAnswer(userAnswer) {
  const raw =
    typeof userAnswer === "object" && userAnswer?.raw
      ? userAnswer.raw
      : typeof userAnswer === "string"
      ? userAnswer
      : userAnswer?.toString?.() ?? "";

  if (!raw || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={formatLatexPreview(raw)} />;
}


export function checkAnswer(task, userAnswer) {
  const normalize = (str) =>
    String(str)
      .replace(/\s/g, "")
      .replace(/\*/g, "")
      .replace(/sqrt\(([^)]+)\)/g, "√$1")
      .replace(/\\sqrt{([^}]+)}/g, "√$1")
      .replace(/\\dfrac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/\\frac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/[()]/g, "")
      .replace(/\\/g, "")
      .toLowerCase();

  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const parsedUser = parseRootFractionInput(raw);
  const parsedExpected = parseRootFractionInput(task.expectedAnswer);

  const approxEqual =
    parsedUser != null &&
    parsedExpected != null &&
    Math.abs(parsedUser - parsedExpected) < 0.01;

  return {
    isCorrect: normalize(raw) === normalize(task.expectedAnswer) || approxEqual,
    comment:
      normalize(raw) === normalize(task.expectedAnswer) || approxEqual
        ? null
        : `Oczekiwano: ${formatLatexPreview(task.expectedAnswer)}`,
  };
}

export function renderAnswer({ expectedLatex }) {
  const expr = expectedLatex.split("=").at(-1).trim(); // tylko ostateczny wynik po "="
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={expr} />
    </div>
  );
}

export function renderExplanation(input) {
  const data = input?.data ?? input;
  const { func1, angle1, func2, angle2, value1, value2, expectedLatex } = data;

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Odczytujemy wartości z tabeli:
        <BlockMath math={`\\${func1}(${angle1}^\\circ) = ${value1}`} />
        <BlockMath math={`\\${func2}(${angle2}^\\circ) = ${value2}`} />
      </div>
      <div>
        <strong>Krok 2:</strong> Podstawiamy i obliczamy:
        <BlockMath math={expectedLatex} />
      </div>
    </div>
  );
}
